import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { OptionField } from './OptionField';
import { CustomTextField } from './CustomTextField';
import { Box, Button, Chip, Typography } from '@mui/material';
import { defaultFilters } from 'constants/filters';
import { convertToQueryString, getFormattedFilters } from 'utils/filters';
import { OptionSearchField } from './OptionSearchField';
import { StaticOptionField } from './StaticOptionField';
import { KeywordField } from './KeywordField';
import { AsyncAutocomplete } from './AsyncAutocomplete';
import SearchIcon from '@mui/icons-material/Search';

export const Filters = ({
  filters = defaultFilters,
  currentFilterValues = null,
  onSubmit: onSubmitFilters = () => {},
  onClear = () => {},
}) => {
  const [initialCurrentFilterValues] = useState(currentFilterValues);

  const defaultValues = filters.reduce((acc, current) => {
    if (current.type === 'custom-keyword') {
      const multipleFieldValue = Object.values(current.field).reduce(
        (acc, current) => ({
          ...acc,
          [current.field]: current.value || null,
        }),
        {}
      );

      return { ...acc, ...multipleFieldValue };
    }
    return { ...acc, [current.field]: current.value || null };
  }, {});

  const { handleSubmit, control, reset, getValues, watch, setValue } = useForm({
    defaultValues,
  });

  const getSelectedFilters = () => getFormattedFilters(filters, getValues());

  const [selectedFilters, setSelectedFilters] = useState(() =>
    getSelectedFilters()
  );

  useEffect(() => {
    if (initialCurrentFilterValues?.length) {
      setSelectedFilters(initialCurrentFilterValues);
    }
  }, [initialCurrentFilterValues]);

  const onSubmit = () => {
    const selectedFiltersObject = getSelectedFilters();
    setSelectedFilters(selectedFiltersObject);

    const selectedFiltersQueryString = convertToQueryString(
      selectedFiltersObject
    );

    onSubmitFilters(selectedFiltersQueryString, selectedFiltersObject);
  };

  const resetFilter = () => {
    reset();
    onClear();
    onSubmitFilters();
    setSelectedFilters([]);
  };

  return (
    <Box
      as="form"
      paddingTop="20px"
      paddingLeft={2}
      paddingRight={2}
      onSubmit={handleSubmit(onSubmit)}>
      <Typography
        variant="title1"
        sx={{
          color: '#405A6D',
          fontSize: '15px',
          lineHeight: '24px',
        }}>
        Filtros
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: '10px',
          gridTemplateColumns: 'repeat(auto-fill, minmax(156px, 1fr))',
        }}>
        {filters.map(
          ({
            type,
            label,
            field,
            options,
            fieldProps,
            requestUrl,
            responseKey,
          }) => {
            if (type === 'asyncOptionSearch')
              return (
                <OptionSearchField
                  key={field}
                  watch={watch}
                  field={field}
                  setValue={setValue}
                  requestUrl={requestUrl}
                  responseKey={responseKey}
                  control={control}
                  fieldProps={fieldProps}
                />
              );

            if (type === 'asyncOption')
              return (
                <OptionField
                  key={field}
                  field={field}
                  requestUrl={requestUrl}
                  responseKey={responseKey}
                  control={control}
                />
              );

            if (type === 'asyncAutocomplete')
              return (
                <AsyncAutocomplete
                  key={field}
                  label={label}
                  field={field}
                  requestUrl={requestUrl}
                  responseKey={responseKey}
                  control={control}
                />
              );

            if (type === 'option')
              return (
                <StaticOptionField
                  key={field}
                  field={field}
                  label={label}
                  options={options}
                  control={control}
                />
              );

            if (['text', 'conditional-text'].includes(type)) {
              return (
                <CustomTextField
                  key={field}
                  label={label}
                  field={field}
                  control={control}
                />
              );
            }

            if (type === 'custom-keyword') {
              return (
                <KeywordField
                  key={field}
                  label={label}
                  field={field}
                  control={control}
                  watch={watch}
                  setValue={setValue}
                />
              );
            }

            return '';
          }
        )}
        <Button
          sx={{
            width: 'auto',
            height: 'auto',
            borderRadius: '8px',
            color: '#1361A4',
            border: '1px solid #D7E8F7',
            background: 'var(--base-white, #FFF)',
            boxShadow: '0px 1px 2px 0px rgba(16, 24, 40, 0.05)',
            textTransform: 'capitalize',
            fontSize: '16px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '24px',
          }}
          type="submit"
          variant="outlined"
          startIcon={<SearchIcon />}>
          Aplicar Filtro
        </Button>
      </Box>
      <Box
        sx={{
          marginTop: 1,
          marginBottom: 1.5,
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px',
        }}>
        {selectedFilters.map(({ label, chipValue }) => (
          <Chip
            key={chipValue}
            sx={{
              color: '#74727d',
              border: '1px solid #E4E4E4',
              fontSize: '15px',
              background: 'var(--base-white, #FFF)',
              '& .title': {
                fontWeight: 'bold',
              },
            }}
            label={
              <Typography>
                <span className="title">{label}: </span>
                {chipValue}
              </Typography>
            }
            variant="outlined"
          />
        ))}
        {!!selectedFilters.length && (
          <Chip
            sx={{
              color: '#217dcd',
              border: '1px solid #217dcd',
              background: 'var(--base-white, #FFF)',
            }}
            label="Limpar Filtros"
            variant="outlined"
            onClick={resetFilter}
          />
        )}
      </Box>
    </Box>
  );
};
